import { CmsSubsidiary } from '@/components/CmsInterfaces';
import { getTelAnchor } from '@/components/Contact';
import { Icon, IconVariant } from '@/components/Icon';
import { Richtext } from '@/components/Richtext';
import { mapStateIdToName } from '@/helpers/mapStateIdToName';
import classnames from '@/utilities/classnames';
import getCompanyNameByState from '@/utilities/getCompanyNameByState';
import replaceAbbreviations from '@/utilities/replaceAbbreviations';
import React from 'react';

interface SortedSubsidiary extends CmsSubsidiary {
  fullName?: string | undefined;
}
interface ContactFormState {
  federalState: string;
  subsidiaries?: CmsSubsidiary[];
  isMobile: boolean;
}

export default class ContactForm extends React.Component<
  object,
  ContactFormState
> {
  constructor(props) {
    super(props);

    this.state = {
      federalState: '',
      isMobile: false,
    };
  }

  async componentDidMount() {
    const response = await fetch(`/api/data/subsidiaries`);
    if (response.ok) {
      this.setState({ subsidiaries: await response.json() });
    }

    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  // Funktion zur Handhabung der Bildschirmgröße
  handleResize = () => {
    this.setState({
      isMobile: window.innerWidth <= 1200,
    });
  };

  handleOnChangeStateSelect = (value: string): void => {
    this.setState({
      federalState: this.state.subsidiaries
        ? this.state.subsidiaries.filter(
            (federalState) => federalState.state === value
          )[0].state
        : '',
    });
  };

  private get hasValidFederalState(): boolean {
    return this.state.federalState !== 'Ihr Bundesland';
  }

  private get sortedSubsidiaries(): SortedSubsidiary[] {
    if (this.state.subsidiaries) {
      const sorted: SortedSubsidiary[] = this.state.subsidiaries;
      sorted.forEach((subsidiary) => {
        subsidiary.fullName = mapStateIdToName(subsidiary.state);
      });
      sorted.sort((a, b) => {
        if (a.fullName && b.fullName) {
          return a.fullName === b.fullName
            ? 0
            : +(a.fullName > b.fullName) || -1;
        }
        return 0;
      });
      return sorted;
    }
    return [];
  }

  drawAdressData = (state: string): JSX.Element => {
    if (this.state.subsidiaries) {
      const stateData = this.state.subsidiaries.filter(
        (federalState) => federalState.state === state
      )[0];
      const address = stateData?.address;

      if (!stateData || !address) {
        return <></>;
      }

      return (
        <div
          className={classnames('ContactForm__contentContainer', {
            'ContactForm__contentContainer--hidden': !this.hasValidFederalState,
          })}
        >
          <figure className="Image Image--original">
            <div className="Image__container">
              <img
                src={`/assets/logos-llgs/${stateData.state}.svg`}
                className="Image__img"
                alt={`Logo von ${getCompanyNameByState[stateData.state]}`}
                loading="lazy"
              />
            </div>
          </figure>
          <div className="flex">
            <div className="ContactForm__addressContainer">
              <div className="ContactForm__address">
                <span
                  dangerouslySetInnerHTML={replaceAbbreviations(stateData.name)}
                />
                <Richtext html={true}>
                  {replaceAbbreviations(address).__html}
                </Richtext>
              </div>
            </div>
            <div className="ContactForm__addressContainer">
              <div className="Richtext">
                <Icon variant={IconVariant.Hotline} />
                Telefon {getTelAnchor(stateData.telephone)}
                <br />
                {stateData.fax && (
                  <>
                    <Icon variant={IconVariant.Fax} />
                    Telefax {getTelAnchor(stateData.fax, 'fax')}
                    <br />
                  </>
                )}
                <Icon variant={IconVariant.Email} />
                <a
                  href={`mailto:${stateData.email}`}
                  title={`Email an: ${stateData.email}`}
                >
                  {stateData.email}
                </a>
                <br />
                <Icon variant={IconVariant.Website} />
                <a
                  href={stateData.website}
                  target="_blank"
                  aria-label={`Zur Webseite von ${stateData.shortName} (öffnet neues Fenster)`}
                  title={stateData.website}
                  rel="noopener noreferrer"
                >
                  Zur Webseite von {stateData.shortName}
                </a>
                <br />
              </div>
            </div>
            {stateData.authority && (
              <div className="ContactForm__addressContainer ContactForm__addressContainer--full-width">
                <div className="ContactForm__address">
                  <Richtext html={true}>
                    {replaceAbbreviations(stateData.authority).__html}
                  </Richtext>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    } else {
      return <div />;
    }
  };

  render(): JSX.Element {
    return (
      <div className="ContactForm">
        <div className="ContactForm__stateChoice box">
          <div className="ContactForm__buttonContainer">
            <span>Bitte wählen Sie Ihr Bundesland aus!</span>
            <div className="ContactForm__selectWrapper">
              <select
                name="stateSelect"
                id="stateSelect"
                value={this.state.federalState || ''}
                onChange={($event) =>
                  this.handleOnChangeStateSelect($event.target.value)
                }
                className="ContactForm__select"
                aria-label="Bundesland auswählen"
              >
                <option
                  className="ContactForm__option-default"
                  value={''}
                  disabled={true}
                >
                  {this.state.isMobile
                    ? 'Bitte auswählen'
                    : 'Bundesland auswählen'}
                </option>

                {this.state.subsidiaries &&
                  this.sortedSubsidiaries.map((subsidiary, idx: number) => (
                    <option value={subsidiary.state} key={idx}>
                      {subsidiary.fullName}
                    </option>
                  ))}
              </select>
              <Icon variant={IconVariant.ChevronDown} className="select-icon" />
            </div>
          </div>
          {this.hasValidFederalState &&
            this.drawAdressData(this.state.federalState)}
        </div>
      </div>
    );
  }
}
